import { useEffect, useState } from "react";
import { Alert, Button, Skeleton, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { IconArrowLeft, IconCheck, IconCreditCard, IconWallet } from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import useSmsCreditAccess from "@/modules/smsCredit/useSmsCreditAccess";
import PersianSupportNumberInput from "@/components/persianNumberInput";
import { CreateTransactionCreditPayload } from "@/models/payload/wallet";
import Num2persian from "@/services/persianUtiles/convertPriceToPersian";
import useGetWallet from "@/pages/dashboard/home/hooks/useGetWalletInfo";
import useIncreaseWallet from "./hooks/useIncreaseWallet";
import classes from "./increaseWallet.module.css";

const presets = [100000, 200000, 500000, 1000000, 2500000, 5000000];
const schema = Yup.object().shape({
  amount: Yup.number()
    .typeError("مبلغ شارژ را وارد کنید")
    .required("مبلغ شارژ را وارد کنید")
    .integer("مبلغ باید عدد صحیح باشد")
    .min(1000, "حداقل مبلغ شارژ ۱٬۰۰۰ ریال است"),
});
type Props = { defaultAmount?: number; sourceId?: string };

function IncreaseWallet({ defaultAmount, sourceId }: Props) {
  const { increaseWallet, isLoading } = useIncreaseWallet();
  const wallet = useGetWallet();
  const [paymentSourceId] = useState(() => sourceId ?? uuidv4());
  const form = useForm<CreateTransactionCreditPayload>({
    initialValues: { amount: defaultAmount, bankGateway: 3, sourceId: paymentSourceId },
    validate: yupResolver(schema),
  });
  const { setFieldValue } = form;
  useEffect(() => {
    if (defaultAmount !== undefined) setFieldValue("amount", defaultAmount);
  }, [defaultAmount, setFieldValue]);

  const amount = form.values.amount ?? 0;
  const valid = Number.isSafeInteger(amount) && amount >= 1000;
  const balance = wallet.data?.amount;
  const submit = (values: CreateTransactionCreditPayload) => {
    if (!isLoading && valid) increaseWallet(values);
  };

  return <form onSubmit={form.onSubmit(submit)} className={classes.form} dir="rtl">
    <div className={classes.balanceCard}>
      <div className={classes.balanceLabel}><span className={classes.walletIcon}><IconWallet size={23} stroke={1.6} /></span><div><span>اعتبار فعلی شما</span><small>برای ارسال پیامک به مشتریان</small></div></div>
      {wallet.isLoading ? <Skeleton width={110} height={28} /> : <div className={classes.balanceValue}><bdi>{typeof balance === "number" ? balance.toLocaleString("fa-IR") : "—"}</bdi><small>ریال</small></div>}
    </div>
    {wallet.isError && <Alert color="red" p="sm">دریافت موجودی انجام نشد. <Button size="compact-sm" variant="subtle" onClick={() => void wallet.refetch()}>تلاش دوباره</Button></Alert>}
    <section aria-labelledby="recharge-presets-title">
      <div className={classes.sectionHeading}><h3 id="recharge-presets-title">چقدر اعتبار اضافه کنیم؟</h3><span>انتخاب سریع مبلغ</span></div>
      <div className={classes.presets}>
        {presets.map(value => <button key={value} type="button" className={classes.preset} aria-pressed={amount === value} disabled={isLoading} onClick={() => { form.setFieldValue("amount", value); form.clearFieldError("amount"); }}>
          <span className={classes.selectedIcon}>{amount === value && <IconCheck size={12} />}</span>
          <strong>{value.toLocaleString("fa-IR")}</strong><small>ریال</small>
        </button>)}
      </div>
    </section>
    <div className={classes.customAmount}>
      <PersianSupportNumberInput
        withThousandsSeparator
        withAsterisk
        label="مبلغ دلخواه"
        description="مبلغ موردنظر را به ریال وارد کنید."
        value={form.values.amount?.toString() ?? ""}
        onChangeHandler={value => form.setFieldValue("amount", value ? Number(value) : undefined)}
        error={form.errors.amount}
        disabled={isLoading}
        size="md"
        leftSection={<Text size="xs" c="dimmed" className={classes.currencySuffix}>ریال</Text>}
        leftSectionWidth={52}
        leftSectionPointerEvents="none"
        styles={{ input: { direction: "ltr", textAlign: "left", paddingLeft: 16, paddingRight: 50, borderRadius: 12, height: 50 }, label: { fontWeight: 600, fontSize: 13 }, description: { fontSize: 11, marginBottom: 8 } }}
      />
      <div className={classes.amountWords} aria-live="polite">{valid ? Num2persian(String(amount)) + " ریال" : "حداقل مبلغ شارژ ۱٬۰۰۰ ریال"}</div>
    </div>
    <div className={classes.paymentSummary} aria-live="polite">
      <div><span>اعتبار پس از پرداخت</span><strong>{typeof balance === "number" && valid ? <><bdi>{(balance + amount).toLocaleString("fa-IR")}</bdi><small>ریال</small></> : "—"}</strong></div>
      <div className={classes.total}><span>مبلغ قابل پرداخت</span><strong><bdi>{valid ? amount.toLocaleString("fa-IR") : "—"}</bdi><small>ریال</small></strong></div>
    </div>
    <div className={classes.footer}>
      <Button color="violet" size="md" radius="md" type="submit" fullWidth loading={isLoading} disabled={!valid} leftSection={<IconCreditCard size={20} />} rightSection={<IconArrowLeft size={17} />}>ادامه و پرداخت</Button>
      <p>برای پرداخت به درگاه بانکی منتقل می‌شوید.</p>
    </div>
  </form>;
}
export default function ManagerIncreaseWallet(props: Props) {
  const { allowed } = useSmsCreditAccess();
  return allowed ? <IncreaseWallet {...props} /> : null;
}
